/**
 * «Эта неделя» — платёжки поставщикам, как «📅 Срок на этой неделе» в боте
 * (§11.1а ТЗ `ops`, D88): дробление, перенос с выходных и порядок строит
 * сервер правилами владельца, экран их только показывает.
 *
 * «Оплачено» отмечает все накладные платёжки одной функцией сервиса —
 * той же, что кнопка бота. Второе нажатие (здесь или в боте) получает
 * «Уже отмечено», а не вторую оплату; список перечитывается раз в 30
 * секунд, и платёжка, оплаченная в боте, исчезает отсюда сама.
 *
 * **Период** (просьба владельца 22.09.2026): «с» и «по» — и видно, сколько
 * платить завтра или на следующей неделе. Считает сервер, по дню платежа.
 *
 * **Вид и галочки** (просьба владельца 22.09.2026). Вид считает сервер по
 * правилам владельца (`supplier_rules.week_payments`): отбирает накладные
 * по дню платежа и собирает из них платёжки. Галочки — оплата выбранных
 * накладных разом, у разных поставщиков: отдельной записью на каждую, той же
 * функцией сервиса. Сумма выбранного — сложение остатков, пришедших с
 * сервера, целыми копейками; окончательную называет сервер в ответе.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { Empty, ErrorState, Loading } from '@shell/states';
import { rub } from '../../api';
import { Link } from '../../router';
import { Notice, Pill, isoDate, when } from '../../ui';
import { LIVE, paymentsApi } from './api';
import type { Order, OrderPaid, PayInvoice, Week as WeekData, WeekView } from './api';
import { cardPath } from './paths';

export const weekKey = ['payments', 'week'] as const;

type View = WeekView;

const VIEWS: { code: View; title: string }[] = [
  { code: 'all', title: 'Всё к оплате' },
  { code: 'due', title: 'Срок на этой неделе' },
  { code: 'late', title: 'Просроченные' },
];

function ordersWord(count: number): string {
  if (count % 10 === 1 && count % 100 !== 11) return 'платёжка';
  if (count % 10 >= 2 && count % 10 <= 4 && !(count % 100 >= 12 && count % 100 <= 14)) {
    return 'платёжки';
  }
  return 'платёжек';
}

/** «1 200,50» из сервера → копейки. Только сложение уже посчитанных сумм. */
function kopecks(value: string): number {
  const [whole, cents = '0'] = value.split('.');
  const sign = whole.startsWith('-') ? -1 : 1;
  return sign * (Math.abs(Number(whole)) * 100 + Number(cents.padEnd(2, '0').slice(0, 2)));
}

function fromKopecks(value: number): string {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

function sumOf(values: string[]): string {
  return fromKopecks(values.reduce((total, value) => total + kopecks(value), 0));
}

/** ISO-дата через `n` дней от сегодня — для быстрых кнопок периода. */
function day(shift: number): string {
  const value = new Date();
  value.setDate(value.getDate() + shift);
  return value.toISOString().slice(0, 10);
}

/** Понедельник и воскресенье недели, сдвинутой на `weeks` вперёд. */
function weekOf(weeks: number): { start: string; end: string } {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7) + weeks * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: monday.toISOString().slice(0, 10), end: sunday.toISOString().slice(0, 10) };
}

export function Week() {
  const client = useQueryClient();
  const [view, setView] = useState<View>('all');
  const [period, setPeriod] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [chosen, setChosen] = useState<Map<number, PayInvoice>>(new Map());
  const [asking, setAsking] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  // Период главнее вида: выбран — показываем ровно его.
  const span = period.end ? period : null;
  const query = useQuery<WeekData, ApiError>({
    queryKey: [...weekKey, view, span],
    queryFn: () => paymentsApi.week(view, span ?? {}),
    placeholderData: (previous) => previous,
    ...LIVE,
  });

  const setDays = (start: string, end: string) => {
    setPeriod({ start, end });
    setResult(null);
  };

  const payMany = useMutation<OrderPaid, ApiError, number[]>({
    mutationFn: (ids) => paymentsApi.payMany(ids),
    onSuccess: (answer) => {
      setResult({
        ok: answer.problems.length === 0,
        text: [answer.message, ...answer.problems].join(' '),
      });
      setChosen(new Map());
    },
    onError: (error) =>
      setResult({ ok: false, text: error.detail ?? 'Не получилось. Попробуйте ещё раз.' }),
    onSettled: () => {
      setAsking(false);
      client.invalidateQueries({ queryKey: ['payments'] });
    },
  });

  if (query.isPending) return <Loading />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const week = query.data;
  // Пока едет ответ на новый период, прошлые числа не показываем: под
  // заголовком «Период 24.09–24.09» они читались как ответ на него
  // (владелец, 23.09.2026, кнопка «Завтра»). Поля периода при этом
  // остаются на месте — их только что трогали.
  const loading = query.isPlaceholderData;

  const suppliers = week.suppliers;
  const orders = week.orders;
  const total = week.total;

  const toggle = (invoices: PayInvoice[], on: boolean) => {
    setResult(null);
    setChosen((old) => {
      const next = new Map(old);
      invoices.forEach((invoice) => (on ? next.set(invoice.id, invoice) : next.delete(invoice.id)));
      return next;
    });
  };
  const picked = [...chosen.values()];
  const pickedSuppliers = new Set(picked.map((invoice) => invoice.supplier.id)).size;
  const pickedTotal = sumOf(picked.map((invoice) => invoice.balance));

  return (
    <div className={`pay-stack${picked.length ? ' pay-stack--picking' : ''}`}>
      <div className="pay-summary">
        <div>
          <p className="pay-summary__label">
            {span ? 'Период' : 'Неделя'} {isoDate(week.start).slice(0, 5)}–
            {isoDate(week.end).slice(0, 5)}
          </p>
          <p className="pay-summary__value">
            {loading ? 'считаю…' : `${orders} ${ordersWord(orders)} · ${rub(total)}`}
          </p>
        </div>
        <p className="faint pay-summary__hint">
          {span && 'Накладные, чей срок (дата + отсрочка поставщика) попадает в период.'}
          {!span && view === 'all' && 'В неделю входит и просроченное — его платить тоже.'}
          {!span &&
            view === 'due' &&
            'Накладные, чей срок (дата + отсрочка поставщика) приходится на эту неделю.'}
          {!span && view === 'late' && 'Накладные, чей срок выпал на прошлые недели, а оплаты нет.'}
          {week.synced_at && <> Данные StoreHouse от {when(week.synced_at)}.</>}
        </p>
      </div>

      <div className="chips" role="tablist" aria-label="Какие платёжки показать">
        {VIEWS.map((item) => (
          <button
            key={item.code}
            type="button"
            role="tab"
            aria-selected={!span && item.code === view}
            className={`chip${!span && item.code === view ? ' chip--active' : ''}`}
            onClick={() => {
              setPeriod({ start: '', end: '' });
              setView(item.code);
            }}
          >
            {item.title}
          </button>
        ))}
      </div>

      <div className="pay-period">
        <label className="mini-field">
          <span>Период с</span>
          <input
            className="input"
            type="date"
            value={period.start}
            onChange={(event) => setPeriod({ ...period, start: event.target.value })}
            aria-label="Период с"
          />
        </label>
        <label className="mini-field">
          <span>по</span>
          <input
            className="input"
            type="date"
            value={period.end}
            onChange={(event) => setPeriod({ ...period, end: event.target.value })}
            aria-label="Период по"
          />
        </label>
        <div className="pay-period__quick">
          <button
            type="button"
            className="button button--small"
            onClick={() => setDays(day(1), day(1))}
          >
            Завтра
          </button>
          <button
            type="button"
            className="button button--small"
            onClick={() => {
              const next = weekOf(1);
              setDays(next.start, next.end);
            }}
          >
            Следующая неделя
          </button>
          {span && (
            <button
              type="button"
              className="button button--small button--quiet"
              onClick={() => setDays('', '')}
            >
              Эта неделя
            </button>
          )}
        </div>
      </div>

      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}

      {loading ? (
        <Loading />
      ) : suppliers.length === 0 ? (
        <Empty
          title={
            span
              ? 'За этот период платить нечего'
              : view === 'late'
                ? 'Просроченных нет'
                : 'На этой неделе платить некому'
          }
          text={
            span
              ? 'Нет накладных, чей срок попадает в выбранные дни. Проверьте другой период.'
              : view === 'late'
                ? 'Все платёжки со сроком в пределах недели.'
                : 'Неоплаченных проведённых накладных со сроком до воскресенья нет.'
          }
        />
      ) : (
        suppliers.map((supplier) => {
          const invoices = supplier.orders.flatMap((order) => order.invoices);
          const all = invoices.every((invoice) => chosen.has(invoice.id));
          return (
            <section className="pay-card" key={supplier.supplier_id}>
              <header className="pay-card__head">
                <label className="pay-check">
                  <input
                    type="checkbox"
                    checked={all}
                    onChange={(event) => toggle(invoices, event.target.checked)}
                    aria-label={`Выбрать все накладные «${supplier.supplier_name}»`}
                  />
                  <h2 className="pay-card__title">{supplier.supplier_name}</h2>
                </label>
                <span className="pay-card__total">{rub(supplier.total)}</span>
              </header>
              {supplier.orders.length > 1 && (
                <p className="faint">
                  {supplier.orders.length} {ordersWord(supplier.orders.length)}
                  {supplier.split ? ' — счёт в том же банке, платёжки дробятся' : ''}
                </p>
              )}
              {supplier.orders.map((order, index) => (
                <OrderRow
                  key={`${order.first_id}:${order.invoice_ids.length}`}
                  order={order}
                  part={
                    supplier.orders.length > 1 ? `${index + 1} из ${supplier.orders.length}` : null
                  }
                  chosen={chosen}
                  onToggle={toggle}
                  view={view}
                  period={span}
                />
              ))}
            </section>
          );
        })
      )}

      {picked.length > 0 && (
        <div className="pay-picked" role="region" aria-label="Выбранные накладные">
          {asking ? (
            <>
              <p>
                Отметить оплату {picked.length} накл. у {pickedSuppliers}{' '}
                {pickedSuppliers === 1 ? 'поставщика' : 'поставщиков'} на {rub(pickedTotal)}? Оплата
                запишется на каждую накладную отдельно, датой сегодня.
              </p>
              <div className="order__buttons">
                <button type="button" className="button" onClick={() => setAsking(false)}>
                  Отмена
                </button>
                <button
                  type="button"
                  className="button button--primary"
                  disabled={payMany.isPending}
                  onClick={() => payMany.mutate(picked.map((invoice) => invoice.id))}
                >
                  {payMany.isPending ? 'Отмечаю…' : 'Да, отметить оплату'}
                </button>
              </div>
            </>
          ) : (
            <>
              <p>
                Выбрано: {picked.length} накл. у {pickedSuppliers}{' '}
                {pickedSuppliers === 1 ? 'поставщика' : 'поставщиков'} · {rub(pickedTotal)}
              </p>
              <div className="order__buttons">
                <button type="button" className="button" onClick={() => setChosen(new Map())}>
                  Снять выбор
                </button>
                <button
                  type="button"
                  className="button button--primary"
                  onClick={() => setAsking(true)}
                >
                  Отметить оплату
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function OrderRow({
  order,
  part,
  chosen,
  onToggle,
  view,
  period,
}: {
  order: Order;
  part: string | null;
  chosen: Map<number, PayInvoice>;
  onToggle: (invoices: PayInvoice[], on: boolean) => void;
  view: View;
  period: { start: string; end: string } | null;
}) {
  const client = useQueryClient();
  const [asking, setAsking] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const pay = useMutation<OrderPaid, ApiError>({
    mutationFn: () => paymentsApi.payOrder(order, view, period ?? {}),
    onSuccess: (answer) => {
      setResult({
        ok: answer.problems.length === 0,
        text: [answer.message, ...answer.problems].join(' '),
      });
    },
    onError: (error) =>
      setResult({ ok: false, text: error.detail ?? 'Не получилось. Попробуйте ещё раз.' }),
    onSettled: () => {
      setAsking(false);
      client.invalidateQueries({ queryKey: ['payments'] });
    },
  });

  return (
    <div className={`order${order.overdue ? ' order--late' : ''}`}>
      <div className="order__line">
        <span className="order__amount">{rub(order.amount)}</span>
        <span className="order__due">
          {part && <span className="faint">{part} · </span>}
          срок {isoDate(order.pay_by).slice(0, 5)} {order.pay_by_weekday}
        </span>
        {order.overdue && <Pill colour="red">просрочен</Pill>}
      </div>
      <ul className="order__invoices">
        {order.invoices.map((invoice) => (
          <li key={invoice.id}>
            <label className="pay-check">
              <input
                type="checkbox"
                checked={chosen.has(invoice.id)}
                onChange={(event) => onToggle([invoice], event.target.checked)}
                aria-label={`Выбрать накладную ${invoice.external_number ?? invoice.number}`}
              />
            </label>
            <Link to={cardPath(invoice.id)} className="order__invoice">
              накл. {invoice.external_number ?? invoice.number}{' '}
              {/* Дата — жирным: по ней владелец сверяет платёжку (22.09.2026). */}
              <strong className="order__date">
                от {isoDate(invoice.doc_date)}
                {invoice.backdated ? '*' : ''}
              </strong>
            </Link>
            <span className="num">{rub(invoice.balance)}</span>
          </li>
        ))}
      </ul>
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
      {asking ? (
        <div className="order__confirm">
          <p>
            Отметить оплату {order.invoices.length} накл. на {rub(order.amount)}? Оплата запишется
            на каждую накладную отдельно, датой сегодня.
          </p>
          <div className="order__buttons">
            <button type="button" className="button" onClick={() => setAsking(false)}>
              Отмена
            </button>
            <button
              type="button"
              className="button button--primary"
              disabled={pay.isPending}
              onClick={() => pay.mutate()}
            >
              Да, отметить оплату
            </button>
          </div>
        </div>
      ) : (
        <div className="order__buttons">
          <button
            type="button"
            className="button button--primary button--small"
            onClick={() => {
              setResult(null);
              setAsking(true);
            }}
          >
            Оплачено
          </button>
        </div>
      )}
    </div>
  );
}
